import { type ReadonlySignal, signal } from "@preact/signals-core";
import { urlOf } from "./table_sounds";

// The seconds two copies of a track overlap at its end to make a loop.
export const FADE = 2;
// The seconds a change of track takes to fade out and in.
const SWITCH = 1;
const LEVEL_RAMP = 0.1;
const STEPS = 8;
const KEY = "table-sound";
// The master's own tap and the server's message for it start the track a moment apart, on clocks that may disagree.
const SAME_START = 1500;

export type MusicMessage = {
	type: "music";
	track: string | null;
	started_at: number | null;
	volume: number;
	now: number;
	url?: string;
	seconds?: number;
	name?: string;
};
// `volume` and `off` are the viewer's own; `heard` is whether any sound, music or effect, has reached this board. `blocked` is a sound
// the browser held back; `locked` is a page nobody has touched, which will hold back the next one.
export type TableMusicState = { track: string | null; blocked: boolean; locked: boolean; volume: number; off: boolean; heard: boolean };
// The encoder delay of the effects' MP3s, in samples at 44.1 kHz.
const EFFECT_SKIP = 1105 / 44100;

export type Deps = {
	now: () => number;
	audio: () => HTMLAudioElement;
	context: () => AudioContext;
	storage: Pick<Storage, "getItem" | "setItem">;
	resolve: (key: string) => { url: string; seconds?: number } | undefined;
	fetch: (url: string) => Promise<ArrayBuffer>;
	// Whether the page has had a gesture, so a context made now will run.
	activated: () => boolean;
};
export type TableMusic = {
	state: ReadonlySignal<TableMusicState>;
	apply: (message: MusicMessage) => void;
	effect: (key: string) => void;
	stopEffect: () => void;
	setVolume: (volume: number) => void;
	setOff: (off: boolean) => void;
	// `prepare` is a tap that asks for sound: the context is made inside it, so that the first effect finds it running.
	unlock: (prepare?: boolean) => void;
	destroy: () => void;
};

type Slot = { el: HTMLAudioElement; gain: GainNode; url: string | null; seek: boolean; timer?: ReturnType<typeof setTimeout> };
// `origin` is when the track began, on this device's clock; two messages that put it within SAME_START of each other are one start.
type Playing = { key: string; startedAt: number | null; origin: number; url: string; seconds?: number };

const defaults: Deps = {
	now: () => Date.now(),
	audio: () => new Audio(),
	context: () => new (globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)(),
	storage: { getItem: (key) => localStorage.getItem(key), setItem: (key, value) => localStorage.setItem(key, value) },
	resolve: (key) => {
		const url = urlOf(key);
		return url ? { url } : undefined;
	},
	fetch: async (url) => {
		const response = await fetch(url);
		if (!response.ok) throw new Error(url);
		return response.arrayBuffer();
	},
	activated: () => navigator.userActivation?.hasBeenActive ?? true,
};

const clamp = (volume: number) => Math.min(100, Math.max(0, Number.isFinite(volume) ? volume : 0));

function readPrefs(storage: Deps["storage"]) {
	try {
		const saved = JSON.parse(storage.getItem(KEY) ?? "null");
		return { volume: typeof saved?.volume === "number" ? clamp(saved.volume) : 100, off: saved?.off === true };
	} catch {
		return { volume: 100, off: false };
	}
}

function ramp(param: AudioParam, to: number, at: number, seconds: number) {
	param.cancelScheduledValues(at);
	param.setValueAtTime(param.value, at);
	param.linearRampToValueAtTime(to, at + seconds);
}

export function createTableMusic(overrides: Partial<Deps> = {}): TableMusic {
	const deps = { ...defaults, ...overrides };
	const cleanups: (() => void)[] = [];
	let ctx: AudioContext | null = null;
	let out: GainNode;
	let slots: Slot[] = [];
	let active = 0;
	let current: Playing | null = null;
	let rejected = false;
	let destroyed = false;

	let key: string | null = null;
	let startedAt: number | null = null;
	let master = 100;
	let base = 0;
	let appliedAt = 0;
	let viewer = readPrefs(deps.storage);
	const buffers = new Map<string, Promise<AudioBuffer>>();
	let shot: AudioBufferSourceNode | null = null;
	let shots = 0;
	let heard = false;
	// An effect came while the browser would not sound it.
	let missed = false;
	// Ends with a gesture that reaches `unlock` or with a context that runs, never with the page's activation alone: Tab activates too.
	let locked = !deps.activated();

	const state = signal<TableMusicState>({ track: null, blocked: false, locked, volume: viewer.volume, off: viewer.off, heard });

	// Every level is a gain: an iPhone ignores HTMLMediaElement.volume.
	const level = () => (master / 100) ** 2 * (viewer.volume / 100) ** 2;

	function listen(target: EventTarget, type: string, run: () => void) {
		target.addEventListener(type, run);
		cleanups.push(() => target.removeEventListener(type, run));
	}

	function wanted(): Playing | null {
		const found = key && !viewer.off ? deps.resolve(key) : undefined;
		return key && found ? { key, startedAt, origin: startedAt === null ? 0 : appliedAt - base, ...found } : null;
	}

	function refresh() {
		const stalled = ctx?.state !== "running";
		if (!stalled) missed = locked = false;
		const blocked = Boolean((wanted() && ctx && (rejected || stalled)) || (missed && stalled));
		state.value = { track: key, blocked, locked, volume: viewer.volume, off: viewer.off, heard };
	}

	function length(slot: Slot) {
		const seconds = current?.seconds ?? slot.el.duration;
		// A track too short to overlap with itself just restarts at its end.
		return seconds >= 2 * FADE ? seconds - FADE : seconds;
	}

	// ponytail: the offset is found once, at the start; a laptop that sleeps through the music rejoins late until the next change.
	function offset(slot: Slot) {
		const loop = length(slot);
		if (!Number.isFinite(loop) || loop <= 0) return 0;
		return (Math.max(0, base + deps.now() - appliedAt) / 1000) % loop;
	}

	function play(slot: Slot) {
		const started = current;
		slot.el.play().catch(() => {
			if (current !== started) return;
			rejected = true;
			current = null;
			refresh();
		});
	}

	function stop(slot: Slot) {
		clearTimeout(slot.timer);
		slot.timer = undefined;
		slot.seek = false;
		slot.url = null;
		slot.el.pause();
		slot.el.removeAttribute("src");
		slot.el.load();
	}

	// The idle element loads the track ahead, so a loop's crossfade finds it ready.
	function preload() {
		const idle = slots[1 - active];
		if (current && !idle.url && !idle.timer) idle.el.src = idle.url = current.url;
	}

	function fadeOut(slot: Slot) {
		clearTimeout(slot.timer);
		ramp(slot.gain.gain, 0, (ctx as AudioContext).currentTime, SWITCH);
		slot.timer = setTimeout(() => {
			stop(slot);
			preload();
		}, SWITCH * 1000);
	}

	function silence() {
		current = null;
		rejected = false;
		for (const slot of slots) if (slot.url) (viewer.off ? stop : fadeOut)(slot);
	}

	function begin(want: Playing, c: AudioContext) {
		const old = slots[active];
		const next = slots[1 - active];
		stop(next);
		if (old.url) fadeOut(old);
		active = 1 - active;
		current = want;
		rejected = false;
		next.seek = true;
		next.el.src = next.url = want.url;
		const fade = next.gain.gain;
		fade.cancelScheduledValues(c.currentTime);
		fade.setValueAtTime(0, c.currentTime);
		fade.linearRampToValueAtTime(1, c.currentTime + SWITCH);
		play(next);
	}

	// Equal power: the two gains follow a quarter sine and cosine, so the sum of their squares stays 1.
	function crossfade(from: Slot, seconds: number) {
		const c = ctx as AudioContext;
		const next = slots[1 - active];
		const [down, up] = [from.gain.gain, next.gain.gain];
		const t = c.currentTime;
		down.cancelScheduledValues(t);
		down.setValueAtTime(1, t);
		up.cancelScheduledValues(t);
		up.setValueAtTime(0, t);
		for (let step = 1; step <= STEPS; step++) {
			const angle = (step / STEPS) * (Math.PI / 2);
			down.linearRampToValueAtTime(Math.cos(angle), t + (seconds * step) / STEPS);
			up.linearRampToValueAtTime(Math.sin(angle), t + (seconds * step) / STEPS);
		}
		// The idle slot may still be fading out the previous track: its cleanup must not reach the copy that starts now.
		clearTimeout(next.timer);
		next.timer = undefined;
		const { url } = current as Playing;
		if (next.url !== url) next.el.src = next.url = url;
		// A late event starts the copy at its lateness, so the loop keeps its length.
		next.el.currentTime = Math.max(0, FADE - seconds);
		play(next);
		active = 1 - active;
	}

	function graph() {
		if (ctx) return ctx;
		let c: AudioContext;
		try {
			c = deps.context();
		} catch {
			return null;
		}
		ctx = c;
		out = c.createGain();
		out.gain.value = level();
		out.connect(c.destination);
		slots = [0, 1].map(() => {
			const el = deps.audio();
			// Without it Web Audio plays silence for media from another host (the uploads bucket).
			el.crossOrigin = "anonymous";
			el.preload = "auto";
			el.loop = false;
			const gain = c.createGain();
			gain.gain.value = 0;
			c.createMediaElementSource(el).connect(gain);
			gain.connect(out);
			const slot: Slot = { el, gain, url: null, seek: false };
			listen(el, "loadedmetadata", () => {
				if (slot.seek) {
					slot.seek = false;
					el.currentTime = offset(slot);
				}
				if (slot === slots[active]) preload();
			});
			listen(el, "timeupdate", () => {
				if (!current || slot !== slots[active]) return;
				const seconds = current.seconds ?? el.duration;
				if (seconds >= 2 * FADE && seconds - el.currentTime <= FADE) crossfade(slot, seconds - el.currentTime);
			});
			listen(el, "ended", () => {
				if (!current) return;
				if (slot === slots[active]) {
					el.currentTime = 0;
					play(slot);
				} else {
					el.pause();
					el.currentTime = 0;
				}
			});
			return slot;
		});
		listen(c, "statechange", () => {
			if (c.state === "running") sync();
			else {
				// Whatever was playing has stalled, so the next start picks the offset again.
				current = null;
				cutEffect();
				refresh();
			}
		});
		return c;
	}

	// `force` is for a gesture: the elements must start inside it, before the context has finished resuming.
	function sync(force = false) {
		if (destroyed) return;
		const want = wanted();
		const c = want ? graph() : ctx;
		if (force) c?.resume().catch(() => {});
		if (!want || !c) silence();
		else if (!(current?.key === want.key && Math.abs(current.origin - want.origin) < SAME_START) && (force || c.state === "running")) begin(want, c);
		refresh();
	}

	function decoded(key: string, url: string, c: AudioContext) {
		let buffer = buffers.get(key);
		if (!buffer) {
			buffer = deps.fetch(url).then((data) => c.decodeAudioData(data));
			buffers.set(key, buffer);
			buffer.catch(() => buffers.delete(key));
		}
		return buffer;
	}

	function cutEffect() {
		shots++;
		shot?.stop();
		shot = null;
	}

	async function effect(name: string) {
		const found = deps.resolve(name);
		if (destroyed || !found) return;
		if (!heard) {
			heard = true;
			refresh();
		}
		if (viewer.off || level() === 0) return;
		const c = ctx ?? (deps.activated() && !locked ? graph() : null);
		if (c?.state !== "running") {
			missed = true;
			refresh();
			return;
		}
		cutEffect();
		const mine = shots;
		let buffer: AudioBuffer;
		try {
			buffer = await decoded(name, found.url, c);
		} catch {
			return;
		}
		if (mine !== shots || destroyed || c.state !== "running") return;
		const source = c.createBufferSource();
		source.buffer = buffer;
		source.connect(out);
		source.start(0, EFFECT_SKIP);
		shot = source;
	}

	function save() {
		try {
			deps.storage.setItem(KEY, JSON.stringify(viewer));
		} catch {
			// Blocked storage only loses the preference.
		}
	}

	// An effect plays through the same output gain, so it follows the level; at zero it is cut rather than left to finish unheard.
	function setLevel() {
		if (ctx) ramp(out.gain, level(), ctx.currentTime, LEVEL_RAMP);
		if (level() === 0) cutEffect();
	}

	return {
		state,
		apply(message) {
			if (destroyed) return;
			// Silence after a track is a Stop; a silent message that only moves the table's volume leaves an effect be.
			if (message.track === null && key !== null) cutEffect();
			if (message.track !== null) heard = true;
			key = message.track;
			startedAt = message.started_at;
			master = clamp(message.volume);
			base = startedAt === null ? 0 : message.now - startedAt;
			appliedAt = deps.now();
			setLevel();
			sync();
		},
		effect: (name) => void effect(name),
		stopEffect: cutEffect,
		setVolume(volume) {
			if (destroyed) return;
			viewer = { ...viewer, volume: clamp(volume) };
			save();
			setLevel();
			refresh();
		},
		setOff(off) {
			if (destroyed) return;
			viewer = { ...viewer, off };
			if (off) {
				cutEffect();
				missed = false;
			}
			save();
			sync(true);
		},
		unlock(prepare = false) {
			if (destroyed) return;
			// An effect missed before any gesture left no context to resume.
			if (deps.activated()) locked = false;
			if ((missed || (prepare && !viewer.off)) && deps.activated()) graph();
			if (state.value.blocked || state.value.locked) sync(true);
			else if (ctx && ctx.state !== "running") ctx.resume().catch(() => {});
		},
		destroy() {
			if (destroyed) return;
			destroyed = true;
			current = null;
			for (const slot of slots) stop(slot);
			for (const cleanup of cleanups) cleanup();
			ctx?.close().catch(() => {});
		},
	};
}
